import { useEffect, useState } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Container,
  Link,
  List,
  ListItemButton,
  ListItemText,
  Stack,
  Step,
  StepLabel,
  Stepper,
  Typography,
} from '@mui/material';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import { useQuery } from '@tanstack/react-query';
import { Link as RouterLink, useNavigate, useSearchParams } from 'react-router-dom';
import {
  connectionAuthorizeUrl,
  listCalendars,
  listProviders,
} from '@/api/endpoints';
import { useMe } from '@/hooks/useMe';
import { useOnboardingGate } from '@/hooks/useOnboardingGate';
import { RuleWizard } from '@/components/rules/RuleWizard';

const STEPS = ['Connect a provider', 'Pick calendars', 'Create your first rule'];

/**
 * Forced 3-step wizard for the empty-state user. Step 1 finishes when the
 * user has at least one connection; the OAuth round-trip is a full-page
 * redirect back to /connections?connected=... so we re-enter here with a
 * populated list.
 */
export function OnboardingPage() {
  const navigate = useNavigate();
  const { data: me } = useMe();
  const { connections } = useOnboardingGate();
  const providers = useQuery({ queryKey: ['providers'], queryFn: listProviders });
  const [params] = useSearchParams();
  const [step, setStep] = useState(0);

  // If we land here with a `connected=` query param, advance step 1 → step 2.
  useEffect(() => {
    if (params.get('connected') && (connections?.length ?? 0) > 0 && step < 1) {
      setStep(1);
    }
  }, [params, connections, step]);

  // Once they exit onboarding via the wizard completing, they'll be navigated
  // by the rule create flow. Bail out and send to /connections if the user
  // somehow arrives here with connections AND we shouldn't be onboarding.
  useEffect(() => {
    if (!params.get('connected') && (connections?.length ?? 0) > 0 && step === 0) {
      setStep(1);
    }
  }, [connections, params, step]);

  return (
    <Container maxWidth="md" disableGutters>
      <Box sx={{ mb: 3 }}>
        <Typography variant="overline" color="primary">
          Welcome to Cadence
        </Typography>
        <Typography variant="h4" component="h1" gutterBottom>
          Let's get you set up
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Three quick steps and Cadence will start mirroring busy time between your calendars.
        </Typography>
      </Box>

      <Stepper activeStep={step} sx={{ mb: 4 }} alternativeLabel>
        {STEPS.map((label) => (
          <Step key={label}>
            <StepLabel>{label}</StepLabel>
          </Step>
        ))}
      </Stepper>

      {step === 0 && (
        <Card variant="outlined">
          <CardContent>
            <Typography variant="h6" gutterBottom>
              Pick a provider to connect
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Cadence will redirect you to the provider so you can grant calendar access. We only store an OAuth token — never your password.
            </Typography>
            {providers.isLoading && <CircularProgress size={20} />}
            {providers.isSuccess && providers.data.length === 0 && (
              <Typography color="error">
                No providers configured.{' '}
                {me?.is_admin ? (
                  <>
                    Head to{' '}
                    <Link component={RouterLink} to="/admin/providers">
                      Admin → Provider configs
                    </Link>{' '}
                    to add one.
                  </>
                ) : (
                  <>Ask an administrator to add one in Admin → Provider configs.</>
                )}
              </Typography>
            )}
            <List>
              {providers.data?.map((p) => (
                <ListItemButton
                  key={p.id}
                  onClick={() => {
                    window.location.href = connectionAuthorizeUrl(p.id, '/onboarding');
                  }}
                >
                  <ListItemText
                    primary={p.display_name}
                    secondary={p.type_display_name}
                  />
                  <OpenInNewIcon fontSize="small" color="action" />
                </ListItemButton>
              ))}
            </List>
          </CardContent>
        </Card>
      )}

      {step === 1 && (
        <Card variant="outlined">
          <CardContent>
            <Typography variant="h6" gutterBottom>
              Calendars on your connection
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              We've pulled in the list below. You'll choose source and target in the next step.
            </Typography>
            <CalendarPreview connectionId={connections?.[0]?.id ?? null} />
            <Stack direction="row" justifyContent="flex-end" spacing={1} sx={{ mt: 2 }}>
              <Button onClick={() => setStep(0)}>Back</Button>
              <Button variant="contained" onClick={() => setStep(2)}>
                Continue
              </Button>
            </Stack>
          </CardContent>
        </Card>
      )}

      {step === 2 && (
        <RuleWizard
          mode="onboarding"
          onCancel={() => setStep(1)}
          onCreated={() => navigate('/rules')}
        />
      )}
    </Container>
  );
}

function CalendarPreview({ connectionId }: { connectionId: number | null }) {
  const { data, isLoading, error } = useQuery({
    queryKey: ['calendars', connectionId],
    queryFn: () => (connectionId === null ? Promise.resolve([]) : listCalendars(connectionId)),
    enabled: connectionId !== null,
  });
  if (connectionId === null) return null;
  if (isLoading) return <CircularProgress size={20} />;
  if (error) return <Typography color="error">{error.message}</Typography>;
  if (!data || data.length === 0)
    return <Typography color="text.secondary">No calendars returned yet.</Typography>;
  return (
    <List dense>
      {data.map((c) => (
        <ListItemButton key={c.id} disabled>
          <ListItemText primary={c.name} secondary={c.is_primary_default ? 'Primary' : undefined} />
        </ListItemButton>
      ))}
    </List>
  );
}
